import axios from "axios";
import { API_URL } from "@/config";
import { useSubscribeForFile } from "@/utils/useSubscribeForFile";

const store = {
  namespaced: true,
  state: {
    reportsLoading: {}, // object of keys of currently loading reports
  },
  getters: {
    isLoadingKey: (state) => (key) => {
      return !!state.reportsLoading[key];
    },
    isLoading(state) {
      return Object.keys(state.reportsLoading).length > 0;
    },
    lastLoadingReportKey(state) {
      const keys = Object.keys(state.reportsLoading);
      return keys.length ? keys[keys.length - 1] : null;
    },
  },
  mutations: {
    // used in useSubscribeForFile hook
    startedLoadingReport(state, key) {
      state.reportsLoading[key] = true;
    },
    finishedLoadingReport(state, key) {
      delete state.reportsLoading[key];
    },

    // A fresh object, not resetStateToDefault - reportsLoading is mutated in place by the
    // two mutations above, so reusing a shared frozen defaultState reference here would make
    // startedLoadingReport throw on the next report.
    resetState(state) {
      state.reportsLoading = {};
    },
  },

  actions: {
    async presencesOrAbsencesList({ commit, getters }, reportData) {
      const { subscribeForFile } = useSubscribeForFile(getters, commit);

      await subscribeForFile("reports/presencesOrAbsencesList", {
        url: `${API_URL}/reports/presencesOrAbsencesList`,
        method: "POST",
        data: reportData,
      });
    },
    async ackReport(_, dispatchId) {
      const response = await axios.post(`${API_URL}/reportAcks/${dispatchId}/ack`);

      if (!response.status || response.status !== 204) {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },

    async mealsToReleaseExport({ rootGetters, commit }, criteria) {
      const { subscribeForFile } = useSubscribeForFile(rootGetters, commit, true);

      await subscribeForFile("reports/mealsToRelease", {
        url: `${API_URL}/reports/mealsToRelease`,
        method: "POST",
        data: criteria,
      });
    },

    async mealsToReleaseExportByEmail(_, { criteria, emailAddresses, reportType, fontSize }) {
      const response = await axios.post(`${API_URL}/reports/mealsToRelease/${reportType}/send`, {
        criteria,
        emailAddresses,
        fontSize,
      });

      if (!response.data || typeof response.data !== "object") {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },

    async downloadMealsToReleasePdf({ rootGetters, commit }, { criteria, orientation, fontSize }) {
      const { subscribeForFile } = useSubscribeForFile(rootGetters, commit, true);

      await subscribeForFile("reports/downloadMealsToReleaseReport", {
        url: `${API_URL}/reports/mealsToRelease/print`,
        method: "POST",
        data: { ...criteria, orientation, fontSize },
      });
    },

    async printMealsToReleasePdf({ rootGetters, commit }, { criteria, orientation, fontSize }) {
      const { subscribeForFile } = useSubscribeForFile(rootGetters, commit, true);

      await subscribeForFile(
        "reports/printMealsToReleaseReport",
        {
          url: `${API_URL}/reports/mealsToRelease/print`,
          method: "POST",
          data: { ...criteria, orientation, fontSize },
        },
        true,
      );
    },

    async periodicReportExport({ rootGetters, commit }, criteria) {
      const { subscribeForFile } = useSubscribeForFile(rootGetters, commit, true);

      await subscribeForFile(`reports/periodic`, {
        url: `${API_URL}/reports/periodic`,
        method: "POST",
        data: criteria,
      });
    },

    async periodicReportExportByEmail(_, { criteria, emailAddresses, reportType }) {
      const response = await axios.post(`${API_URL}/reports/periodic/${reportType}/send`, {
        criteria,
        emailAddresses,
      });

      if (!response.data || typeof response.data !== "object") {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },

    async nestedMealsToReleaseExport({ rootGetters, commit }, criteria) {
      const { subscribeForFile } = useSubscribeForFile(rootGetters, commit, true);

      await subscribeForFile("reports/nestedMealsToRelease", {
        url: `${API_URL}/reports/nestedMealsToRelease`,
        method: "POST",
        data: criteria,
      });
    },

    async nestedMealsToReleaseCsvExport({ rootGetters, commit }, criteria) {
      const { subscribeForFile } = useSubscribeForFile(rootGetters, commit, true);

      await subscribeForFile("reports/nestedMealsToReleaseCsv", {
        url: `${API_URL}/reports/nestedMealsToRelease/csv`,
        method: "POST",
        data: criteria,
      });
    },

    async downloadNestedMealsToReleasePdf(
      { rootGetters, commit },
      { criteria, orientation, fontSize },
    ) {
      const { subscribeForFile } = useSubscribeForFile(rootGetters, commit, true);

      await subscribeForFile("reports/downloadNestedMealsToReleaseReport", {
        url: `${API_URL}/reports/nestedMealsToRelease/print`,
        method: "POST",
        data: { ...criteria, orientation, fontSize },
      });
    },

    async printNestedMealsToReleasePdf(
      { rootGetters, commit },
      { criteria, orientation, fontSize },
    ) {
      const { subscribeForFile } = useSubscribeForFile(rootGetters, commit, true);

      await subscribeForFile(
        "reports/printNestedMealsToReleaseReport",
        {
          url: `${API_URL}/reports/nestedMealsToRelease/print`,
          method: "POST",
          data: { ...criteria, orientation, fontSize },
        },
        true,
      );
    },

    async nestedMealsToReleaseExportByEmail(_, { criteria, emailAddresses, reportType, fontSize }) {
      const response = await axios.post(
        `${API_URL}/reports/nestedMealsToRelease/${reportType}/send`,
        {
          criteria,
          emailAddresses,
          fontSize,
        },
      );

      if (!response.data || typeof response.data !== "object") {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },

    async usersTransactionsExport({ rootGetters, commit }, criteria) {
      const { subscribeForFile } = useSubscribeForFile(rootGetters, commit, true);

      await subscribeForFile("reports/usersTransactions", {
        url: `${API_URL}/reports/usersTransactions`,
        method: "POST",
        data: criteria,
      });
    },

    async depositsExport({ rootGetters, commit }, criteria) {
      const { subscribeForFile } = useSubscribeForFile(rootGetters, commit, true);

      await subscribeForFile("reports/deposits", {
        url: `${API_URL}/reports/deposits`,
        method: "POST",
        data: criteria,
      });
    },

    async ordersListByEmail(_, { criteria, emailAddresses, reportType }) {
      const response = await axios.post(`${API_URL}/reports/ordersList/${reportType}/send`, {
        criteria,
        emailAddresses,
      });

      if (!response.data || typeof response.data !== "object") {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },

    async presencesListByEmail(_, { criteria, emailAddresses, reportType }) {
      const response = await axios.post(`${API_URL}/reports/presencesList/${reportType}/send`, {
        criteria,
        emailAddresses,
      });

      if (!response.data || typeof response.data !== "object") {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },

    async usersTransactionsListByEmail(_, { criteria, emailAddresses, reportType }) {
      const response = await axios.post(`${API_URL}/reports/usersTransactions/${reportType}/send`, {
        criteria,
        emailAddresses,
      });

      if (!response.data || typeof response.data !== "object") {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },

    async depositsListByEmail(_, { criteria, emailAddresses, reportType }) {
      const response = await axios.post(`${API_URL}/reports/deposits/${reportType}/send`, {
        criteria,
        emailAddresses,
      });

      if (!response.data || typeof response.data !== "object") {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },

    async generateMonthlyBalancesReport({ rootGetters, commit }, criteria) {
      const { subscribeForFile } = useSubscribeForFile(rootGetters, commit, true);

      await subscribeForFile("reports/monthlyBalanceReport", {
        url: `${API_URL}/reports/monthlyBalanceReport`,
        method: "POST",
        data: criteria,
      });
    },

    async generateRefundReport({ rootGetters, commit }, criteria) {
      const { subscribeForFile } = useSubscribeForFile(rootGetters, commit, true);

      await subscribeForFile("reports/refundReport", {
        url: `${API_URL}/reports/refundReport`,
        method: "POST",
        data: criteria,
      });
    },

    async mealsToPrepareExportByEmail(_, { criteria, emailAddresses, reportType, fontSize }) {
      const response = await axios.post(`${API_URL}/reports/mealsToPrepare/${reportType}/send`, {
        criteria,
        emailAddresses,
        fontSize,
      });

      if (!response.data || typeof response.data !== "object") {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },

    async downloadMealsToPreparePdf(
      { rootGetters, commit },
      { criteria, orientation, fontSize, paperFormat },
    ) {
      const { subscribeForFile } = useSubscribeForFile(rootGetters, commit, true);

      await subscribeForFile("reports/downloadMealsToPrepareReport", {
        url: `${API_URL}/reports/mealsToPrepare/print`,
        method: "POST",
        data: { ...criteria, orientation, fontSize, paperFormat },
      });
    },

    async printMealsToPreparePdf(
      { rootGetters, commit },
      { criteria, orientation, fontSize, paperFormat },
    ) {
      const { subscribeForFile } = useSubscribeForFile(rootGetters, commit, true);

      await subscribeForFile(
        "reports/printMealsToPrepareReport",
        {
          url: `${API_URL}/reports/mealsToPrepare/print`,
          method: "POST",
          data: { ...criteria, orientation, fontSize, paperFormat },
        },
        true,
      );
    },

    async exportMealsToPrepare({ rootGetters, commit }, criteria) {
      const { subscribeForFile } = useSubscribeForFile(rootGetters, commit, true);

      await subscribeForFile("reports/generateMealsToPrepareExport", {
        url: `${API_URL}/reports/mealsToPrepare/export`,
        method: "POST",
        data: criteria,
      });
    },

    async downloadOrdersListPdf({ rootGetters, commit }, { criteria, orientation, fontSize }) {
      const { subscribeForFile } = useSubscribeForFile(rootGetters, commit, true);

      await subscribeForFile("reports/downloadOrdersListPdf", {
        url: `${API_URL}/reports/ordersList/print`,
        method: "POST",
        data: { ...criteria, orientation, fontSize },
      });

      return true;
    },

    async printOrdersListPdf({ rootGetters, commit }, { criteria, orientation, fontSize }) {
      const { subscribeForFile } = useSubscribeForFile(rootGetters, commit, true);

      await subscribeForFile(
        "reports/printOrdersListReport",
        {
          url: `${API_URL}/reports/ordersList/print`,
          method: "POST",
          data: { ...criteria, orientation, fontSize },
        },
        true,
      );
    },

    async downloadSubjectOrdersInfoPdf(
      { rootGetters, commit },
      { subjectId, subjectType, criteria, orientation, fontSize },
    ) {
      const { subscribeForFile } = useSubscribeForFile(rootGetters, commit, true);

      await subscribeForFile(`reports/subject/downloadOrdersInfoReport`, {
        url: `${API_URL}/reports/subjectOrdersInfo/${subjectType}/${subjectId}/print`,
        method: "POST",
        data: { ...criteria, orientation, fontSize },
      });
    },

    async printSubjectOrdersInfoPdf(
      { rootGetters, commit },
      { subjectId, subjectType, criteria, orientation, fontSize },
    ) {
      const { subscribeForFile } = useSubscribeForFile(rootGetters, commit, true);

      await subscribeForFile(
        `reports/subject/printOrdersInfoReport`,
        {
          url: `${API_URL}/reports/subjectOrdersInfo/${subjectType}/${subjectId}/print`,
          method: "POST",
          data: { ...criteria, orientation, fontSize },
        },
        true,
      );
    },

    async printOrderedMealsPdf({ rootGetters, commit }, { criteria, orientation, fontSize }) {
      const { subscribeForFile } = useSubscribeForFile(rootGetters, commit, true);

      await subscribeForFile(
        "reports/printOrderedMealsReport",
        {
          url: `${API_URL}/reports/orderedMeals/print`,
          method: "POST",
          data: { ...criteria, orientation, fontSize },
        },
        true,
      );
    },

    async downloadOrderedMealsPdf({ rootGetters, commit }, { criteria, orientation, fontSize }) {
      const { subscribeForFile } = useSubscribeForFile(rootGetters, commit, true);

      await subscribeForFile("reports/downloadOrderedMealsReport", {
        url: `${API_URL}/reports/orderedMeals/print`,
        method: "POST",
        data: { ...criteria, orientation, fontSize },
      });
    },

    async orderedMealsExportByEmail(_, { criteria, emailAddresses, reportType, fontSize }) {
      const response = await axios.post(`${API_URL}/reports/orderedMeals/${reportType}/send`, {
        criteria,
        emailAddresses,
        fontSize,
      });

      if (!response.data || typeof response.data !== "object") {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },

    async exportOrderedMeals({ rootGetters, commit }, criteria = {}) {
      const { subscribeForFile } = useSubscribeForFile(rootGetters, commit, true);

      await subscribeForFile("reports/generateOrderedMealsReport", {
        url: `${API_URL}/reports/orderedMeals/export`,
        method: "POST",
        data: criteria,
      });
    },

    async purchaseReportExportByEmail(_, { criteria, emailAddresses, reportType, fontSize }) {
      const response = await axios.post(`${API_URL}/reports/purchaseReport/${reportType}/send`, {
        criteria,
        emailAddresses,
        fontSize,
      });

      if (!response.data || typeof response.data !== "object") {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },

    async downloadPurchaseReportPdf({ rootGetters, commit }, { criteria, orientation, fontSize }) {
      const { subscribeForFile } = useSubscribeForFile(rootGetters, commit, true);

      await subscribeForFile("reports/downloadPurchaseReport", {
        url: `${API_URL}/reports/purchaseReport/print`,
        method: "POST",
        data: { ...criteria, orientation, fontSize },
      });
    },

    async printPurchaseReportPdf({ rootGetters, commit }, { criteria, orientation, fontSize }) {
      const { subscribeForFile } = useSubscribeForFile(rootGetters, commit, true);

      await subscribeForFile(
        "reports/printPurchaseReport",
        {
          url: `${API_URL}/reports/purchaseReport/print`,
          method: "POST",
          data: { ...criteria, orientation, fontSize },
        },
        true,
      );
    },

    async exportPurchaseReport({ rootGetters, commit }, criteria) {
      const { subscribeForFile } = useSubscribeForFile(rootGetters, commit, true);

      await subscribeForFile("reports/generatePurchaseReportExport", {
        url: `${API_URL}/reports/purchaseReport/export`,
        method: "POST",
        data: criteria,
      });
    },

    async adjustedPurchaseReportExportByEmail(
      _,
      { criteria, emailAddresses, reportType, fontSize },
    ) {
      const response = await axios.post(
        `${API_URL}/reports/purchaseReport/adjusted/${reportType}/send`,
        {
          criteria,
          emailAddresses,
          fontSize,
        },
      );

      if (!response.data || typeof response.data !== "object") {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },

    async downloadAdjustedPurchaseReportPdf(
      { rootGetters, commit },
      { date, includeWarehouse, lines, orientation, fontSize },
    ) {
      const { subscribeForFile } = useSubscribeForFile(rootGetters, commit, true);

      await subscribeForFile("reports/downloadAdjustedPurchaseReport", {
        url: `${API_URL}/reports/purchaseReport/adjusted/print`,
        method: "POST",
        data: { date, includeWarehouse, lines, orientation, fontSize },
      });
    },

    async printAdjustedPurchaseReportPdf(
      { rootGetters, commit },
      { date, includeWarehouse, lines, orientation, fontSize },
    ) {
      const { subscribeForFile } = useSubscribeForFile(rootGetters, commit, true);

      await subscribeForFile(
        "reports/printAdjustedPurchaseReport",
        {
          url: `${API_URL}/reports/purchaseReport/adjusted/print`,
          method: "POST",
          data: { date, includeWarehouse, lines, orientation, fontSize },
        },
        true,
      );
    },

    async exportAdjustedPurchaseReport({ rootGetters, commit }, { date, includeWarehouse, lines }) {
      const { subscribeForFile } = useSubscribeForFile(rootGetters, commit, true);

      await subscribeForFile("reports/generateAdjustedPurchaseReportExport", {
        url: `${API_URL}/reports/purchaseReport/adjusted/export`,
        method: "POST",
        data: { date, includeWarehouse, lines },
      });
    },

    async ticketsExport({ rootGetters, commit }, criteria) {
      const { subscribeForFile } = useSubscribeForFile(rootGetters, commit, true);

      await subscribeForFile("reports/generateTicketsExport", {
        url: `${API_URL}/reports/tickets/export`,
        method: "POST",
        data: criteria,
      });
    },

    async foodCostReportExportByEmail(_, { criteria, emailAddresses, reportType, fontSize }) {
      const response = await axios.post(`${API_URL}/reports/foodCostReport/${reportType}/send`, {
        criteria,
        emailAddresses,
        fontSize,
      });

      if (!response.data || typeof response.data !== "object") {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },

    async downloadFoodCostReportPdf({ rootGetters, commit }, { criteria, orientation, fontSize }) {
      const { subscribeForFile } = useSubscribeForFile(rootGetters, commit, true);

      await subscribeForFile("reports/downloadFoodCostReport", {
        url: `${API_URL}/reports/foodCostReport/print`,
        method: "POST",
        data: { ...criteria, orientation, fontSize },
      });
    },

    async printFoodCostReportPdf({ rootGetters, commit }, { criteria, orientation, fontSize }) {
      const { subscribeForFile } = useSubscribeForFile(rootGetters, commit, true);

      await subscribeForFile(
        "reports/printFoodCostReport",
        {
          url: `${API_URL}/reports/foodCostReport/print`,
          method: "POST",
          data: { ...criteria, orientation, fontSize },
        },
        true,
      );
    },

    async exportFoodCostReport({ rootGetters, commit }, criteria) {
      const { subscribeForFile } = useSubscribeForFile(rootGetters, commit, true);

      await subscribeForFile("reports/generateFoodCostReportExport", {
        url: `${API_URL}/reports/foodCostReport/export`,
        method: "POST",
        data: criteria,
      });
    },

    async productionReportExportByEmail(_, { criteria, emailAddresses, reportType, fontSize }) {
      const response = await axios.post(`${API_URL}/reports/productionReport/${reportType}/send`, {
        criteria,
        emailAddresses,
        fontSize,
      });

      if (!response.data || typeof response.data !== "object") {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },

    async downloadProductionReportPdf(
      { rootGetters, commit },
      { criteria, orientation, fontSize },
    ) {
      const { subscribeForFile } = useSubscribeForFile(rootGetters, commit, true);

      await subscribeForFile("reports/downloadProductionReport", {
        url: `${API_URL}/reports/productionReport/print`,
        method: "POST",
        data: { ...criteria, orientation, fontSize },
      });
    },

    async printProductionReportPdf({ rootGetters, commit }, { criteria, orientation, fontSize }) {
      const { subscribeForFile } = useSubscribeForFile(rootGetters, commit, true);

      await subscribeForFile(
        "reports/printProductionReport",
        {
          url: `${API_URL}/reports/productionReport/print`,
          method: "POST",
          data: { ...criteria, orientation, fontSize },
        },
        true,
      );
    },

    async exportProductionReport({ rootGetters, commit }, criteria) {
      const { subscribeForFile } = useSubscribeForFile(rootGetters, commit, true);

      await subscribeForFile("reports/generateProductionReportExport", {
        url: `${API_URL}/reports/productionReport/export`,
        method: "POST",
        data: criteria,
      });
    },

    async productionReportCsvExport({ rootGetters, commit }, criteria) {
      const { subscribeForFile } = useSubscribeForFile(rootGetters, commit, true);

      await subscribeForFile("reports/generateProductionReportCsv", {
        url: `${API_URL}/reports/productionReport/csv`,
        method: "POST",
        data: criteria,
      });
    },

    async generateAutomatedReport({ rootGetters, commit }, { automatedReportId, date }) {
      const { subscribeForFile } = useSubscribeForFile(rootGetters, commit, true);

      await subscribeForFile("reports/generateAutomatedReport", {
        url: `${API_URL}/automatedReports/${automatedReportId}/generate`,
        method: "POST",
        data: { date },
      });
    },
  },
};

export default store;
